import { useCallback, useEffect, useRef } from "react";

/**
 * Keyboard counterpart of the listing editor rows' mouse-leave autosave (GROWTH-7588).
 *
 * Returns `onBlur` / `onFocus` handlers for a row's container. `onLeave` runs once focus has moved from
 * inside the row to an element outside it (Tab, Shift+Tab, a screen reader moving focus). The move is
 * judged one tick later: a focus event that reaches the row first cancels it, whether focus went to another
 * field of the row or to a menu or picker the row renders in a portal (React bubbles that event to the
 * row although the portal sits elsewhere in the DOM). A blur that sends focus nowhere (a click on non-focusable
 * space such as the date picker's icon, a window switch) is not a leave: mouse-leave, the outside-touch
 * listener and the page-hide flush cover those. A pending leave is dropped if the row unmounts first,
 * so a removed row is never saved.
 *
 * @param {Function} onLeave Saves the row; called with no arguments, always the latest render's closure
 * @returns {{ onBlur: Function, onFocus: Function }}
 */
export const useRowFocusLeave = onLeave => {
  const onLeaveRef = useRef(onLeave);
  const timerRef = useRef(null);

  onLeaveRef.current = onLeave;

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancel, [cancel]);

  const onBlur = useCallback(event => {
    // Only a move to an element counts. A blur to nowhere carries none: null in browsers, the document in jsdom.
    if (event.relatedTarget?.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    // A move to another element of the row is cancelled by that element's focus event, which reaches the row next.
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onLeaveRef.current();
    }, 0);
  }, []);

  return { onBlur, onFocus: cancel };
};
